/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  UserSessionIssuerFieldMismatch,
  UserSessionIssuerFieldMismatch$inboundSchema,
} from "./usersessionissuerfieldmismatch.js";

/**
 * Authoritative impact summary for consolidating one user_session_issuer onto another.
 */
export type OrganizationUserSessionIssuerMigratePreflight = {
  /**
   * True when no hard blocker is present.
   */
  canMigrate: boolean;
  /**
   * Custom CIMD allowlist entries that would move or merge.
   */
  cimdClientCount: number;
  /**
   * Clients that would move.
   */
  clientCount: number;
  /**
   * Active OAuth client ids already present on both issuers. Non-empty blocks migration.
   */
  conflictingClientIds: Array<string>;
  /**
   * User-session consents that would move.
   */
  consentCount: number;
  /**
   * Enterprise-managed authorization bindings already present on the target. Non-zero blocks migration.
   */
  emaBindingConflictCount: number;
  /**
   * Whether a Platform MCP catalog registration owns either issuer. True blocks migration.
   */
  platformOwned: boolean;
  /**
   * Active principal bindings that would violate target uniqueness. Non-zero blocks migration.
   */
  principalBindingConflictCount: number;
  /**
   * Remote-session credentials whose issuer provenance would move.
   */
  remoteSessionCount: number;
  /**
   * User sessions that would move.
   */
  sessionCount: number;
  /**
   * Configuration differences that do not block the migration. The target's values become authoritative for future authorization; existing sessions and refresh tokens are unaffected. Besides the issuer settings, this reports authorization_server_mode, use_authentication_host, and issuer_url (the shared authorization server's issuer identifier, empty when not served) whenever they differ: repointed MCP servers then direct clients to a different authorization server, so anything configured with the source's values, such as an identity provider or a federated platform, must be updated.
   */
  warnings: Array<UserSessionIssuerFieldMismatch>;
  /**
   * Stable fingerprint of the current warnings. Empty when there are no warnings; otherwise pass this exact value to migrateIssuer to confirm them.
   */
  warningsFingerprint: string;
};

/** @internal */
export const OrganizationUserSessionIssuerMigratePreflight$inboundSchema:
  z.ZodMiniType<OrganizationUserSessionIssuerMigratePreflight, unknown> = z
    .pipe(
      z.object({
        can_migrate: z.boolean(),
        cimd_client_count: z.int(),
        client_count: z.int(),
        conflicting_client_ids: z.array(z.string()),
        consent_count: z.int(),
        ema_binding_conflict_count: z.int(),
        platform_owned: z.boolean(),
        principal_binding_conflict_count: z.int(),
        remote_session_count: z.int(),
        session_count: z.int(),
        warnings: z.array(UserSessionIssuerFieldMismatch$inboundSchema),
        warnings_fingerprint: z.string(),
      }),
      z.transform((v) => {
        return remap$(v, {
          "can_migrate": "canMigrate",
          "cimd_client_count": "cimdClientCount",
          "client_count": "clientCount",
          "conflicting_client_ids": "conflictingClientIds",
          "consent_count": "consentCount",
          "ema_binding_conflict_count": "emaBindingConflictCount",
          "platform_owned": "platformOwned",
          "principal_binding_conflict_count": "principalBindingConflictCount",
          "remote_session_count": "remoteSessionCount",
          "session_count": "sessionCount",
          "warnings_fingerprint": "warningsFingerprint",
        });
      }),
    );

export function organizationUserSessionIssuerMigratePreflightFromJSON(
  jsonString: string,
): SafeParseResult<
  OrganizationUserSessionIssuerMigratePreflight,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      OrganizationUserSessionIssuerMigratePreflight$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'OrganizationUserSessionIssuerMigratePreflight' from JSON`,
  );
}
